import * as React from "react";
import {
  DataGrid,
  GridActionsCellItem,
  GridCellParams,
  GridColDef,
  GridSortModel,
  GridValueFormatterParams,
  GridValueGetterParams,
  GridFilterModel,
  useGridApiRef,
} from "@mui/x-data-grid";
import { useEffect, useState } from "react";
import { Box, Modal } from "@mui/material";
import ClientDetailsModal from "./ClientDetailsModal";
import { EditRounded } from "@mui/icons-material";
import { convertToLocalWithoutUTC } from "../../utils/dateFormat";
import { useDispatch } from "react-redux";
import { setSelectedTransaction } from "../../redux/features/transactionsSlice";
import { TransactionDetailsType } from "../../types/genericType";
// import { formatDollar } from "../../utils/utils";

type TransactionTableProps = {
  transactionData: Array<TransactionDetailsType>;
  setOpenEditDrawer: Function;
  auditorView?: boolean;
  setShowClearFilters: Function;
  filterModel: GridFilterModel;
  setFilterModel: Function;
  /** IDs of rows that currently pass the DataGrid filters (used for export). */
  onFilteredTransactionIdsChange?: (_ids: number[]) => void;
};

const TransactionTable = (props: TransactionTableProps) => {
  const {
    transactionData,
    setOpenEditDrawer,
    auditorView,
    setShowClearFilters,
    filterModel,
    setFilterModel,
    onFilteredTransactionIdsChange,
  } = props;
  const [selectedClientDetails, setSelectedClientDetails] = useState<{
    id: string;
    name: string;
  }>({ id: "", name: "" });
  

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [sortModel, setSortModel] = useState<GridSortModel>([
    { field: "date", sort: "desc" },
  ]);
  const dispatch = useDispatch();
  const apiRef = useGridApiRef();

  const initialColumns: GridColDef[] = [
    {
      field: "date",
      headerName: "Date",
      minWidth: 120,
      valueFormatter: (params: GridValueFormatterParams) =>
        convertToLocalWithoutUTC(params.value).format("MM/DD/YYYY"),
      headerClassName: "header-datagrid",
    },
    {
      field: "description",
      headerName: "Description",
      width: 180,
      sortable: false,
      headerClassName: "header-datagrid",
    },
    {
      field: "client",
      headerName: "Client",
      minWidth: 200,
      sortable: false,
      headerClassName: "header-datagrid",
      cellClassName: "client-cell",
    },
    {
      field: "account",
      headerName: "Acct. #",
      width: 70,
      sortable: false,
      headerClassName: "header-datagrid",
    },
    {
      field: "accountName",
      headerName: "Acct. Name",
      minWidth: 150,
      sortable: false,
      headerClassName: "header-datagrid",
    },
    {
      field: "code",
      headerName: "Type",
      width: 120,
      sortable: false,
      headerClassName: "header-datagrid",
    },
    {
      field: "status",
      headerName: "Status",
      width: 100,
      sortable: false,
      headerClassName: "header-datagrid",
      valueFormatter: (params: GridValueFormatterParams) =>
        params.value.toString().substring(0, 1),
    },
    {
      field: "bankTransactionType",
      headerName: "Bank Transaction",
      width: 150,
      sortable: false,
      headerClassName: "header-datagrid",
      // valueFormatter: (params: GridValueFormatterParams) =>
      //   params.value.toString().substring(0, 1),
    },    
    {
      field: "checkNumber",
      headerName: "Check/ACH/Wire",
      width: 180,
      sortable: false,
      headerClassName: "header-datagrid",
      valueGetter: (params: GridValueGetterParams) => {
        if (params.row.checkNumber) {
          return params.row.checkNumber;
        } else if (params.row.wireNumber) {
          return params.row.wireNumber;
        } else if (params.row.achDetails) {
          return params.row.achDetails;
        }
        return "";
      },
    },
    {
      field: "amount",
      headerName: "Amount",
      type: "number",
      minWidth: 160,
      sortable: false,
      valueFormatter: (params: GridValueFormatterParams) =>
        `${params.value > 0 ? "+" : ""}${params.value.toLocaleString("en-US", {
          style: "currency",
          currency: "USD",
        })}`,
      headerClassName: "header-datagrid",
      cellClassName: (params: GridCellParams<any, any>) =>
        params.row.amount >= 0 ? "positive-cell" : "negative-cell",
    },
    {
      field: "balance",
      headerName: "Balance",
      type: "number",
      sortable: false,
      minWidth: 160,
      valueFormatter: (params: GridValueFormatterParams) =>
        `${params.value > 0 ? "+" : ""}${params.value.toLocaleString("en-US", {
          style: "currency",
          currency: "USD",
        })}`,
      headerClassName: "header-datagrid",
    },
  ];

  const [columns, setColumns] = useState<GridColDef[]>(initialColumns);

  useEffect(() => {
    if (auditorView) {
      setColumns(initialColumns.filter((col) => col.field !== "edit"));
    } else {
      setColumns([
        ...initialColumns,
        {
          field: "edit",
          headerName: "Edit",
          sortable: false,
          maxWidth: 60,
          align: "center",
          headerAlign: "center",
          headerClassName: "header-datagrid",
          renderCell: (params: GridCellParams) => {
            if (params.row.status !== "Void") {
              return [
                <GridActionsCellItem
                  icon={<EditRounded sx={{ fontSize: "20px" }} />}
                  label=""
                  onClick={() => handleEditClick(params.row.id)}
                />,
              ];
            } else return [];
          },
        },
      ]);
    }
  }, [auditorView, initialColumns]);

  const handleCellClick = (fromClientId: string, clientName: string) => {
    setSelectedClientDetails({ id: fromClientId, name: clientName });
    setIsModalOpen(true);
  };

  const handleEditClick = (id: number) => {
    const transaction = transactionData.find(
      (transaction) => transaction.id === id
    );
    if (transaction) {
      dispatch(setSelectedTransaction(transaction));
      setOpenEditDrawer(true);
    }
  };

  const handleModalClose = () => {
    setSelectedClientDetails({ id: "", name: "" });
    setIsModalOpen(false);
  };
  const handleFilterModelChange = (newFilterModel: GridFilterModel) => {
    const filtersRemoved = filterModel.items.length > newFilterModel.items.length;
    const filtersAdded = filterModel.items.length < newFilterModel.items.length;

    setFilterModel(newFilterModel);


    if (filtersAdded) {
      setShowClearFilters(true);
    } else if (filtersRemoved) {
      setShowClearFilters(false);
    } else {
      setShowClearFilters(true);
    }
  };

  // Compute which transaction rows are passing the active DataGrid filters.
  // We use `filteredRowsLookup` from the grid internal state (client-side filtering),
  // then bubble just the matching row IDs to the parent so exports match the UI.
  useEffect(() => {
    if (!apiRef.current) return;

    // Wait a tick so the grid can apply the latest `filterModel`
    // and update `filteredRowsLookup`.
    const t = window.setTimeout(() => {
      // If no column filters are active, export should match the UI:
      // send IDs for all rows currently shown in the grid.
      if (!filterModel?.items?.length) {
        onFilteredTransactionIdsChange?.(transactionData.map((row) => row.id));
        return;
      }

      const lookup = apiRef.current.state.filter.filteredRowsLookup;
      const ids = transactionData
        .map((row) => row.id)
        .filter((id) => lookup[id] === true);
      onFilteredTransactionIdsChange?.(ids);
    }, 0);

    return () => window.clearTimeout(t);
  }, [apiRef, transactionData, filterModel, onFilteredTransactionIdsChange]);

  return (
    <>
      <DataGrid
        rows={transactionData}
        columns={columns}
        getRowId={(row) => row.id}
        sortModel={sortModel}
        onSortModelChange={setSortModel}
        pageSizeOptions={[10, 20, 25, 30, 40, 50, 100]}
        className="datagrid"
        apiRef={apiRef}
        onFilterModelChange={handleFilterModelChange}
        onCellClick={(params) =>
          params.field === "client" &&
          handleCellClick(params.row.fromClientId, params.row.client)
        }
        filterModel={filterModel}
      />
      <Modal
        className="account-modal"
        open={isModalOpen}
        onClose={handleModalClose}
      >
        <Box>
          <ClientDetailsModal
            selectedClientDetails={selectedClientDetails}
            handleModalClose={handleModalClose}
          />
        </Box>
      </Modal>
    </>
  );
};

export default TransactionTable;
